import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    effect,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import {
    Mesh,
    MeshBasicMaterial,
    MeshStandardMaterial,
    Object3D,
    Scene,
    Vector3,
    WebGLRenderer,
    Box3,
    BufferGeometry,
    Material,
    GridHelper,
    PerspectiveCamera,
    Color,
    AmbientLight,
    DirectionalLight,
    Euler,
    BoxGeometry,
    WireframeGeometry,
    LineBasicMaterial,
    LineSegments,
    PlaneGeometry,
    DoubleSide,
    Matrix4,
    Frustum,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { CanvasConfig, ZoomConfig } from "@utils/canvas-configuration";
import { Object3DUserData, initialOrientation } from "@utils/shape";
import { ComponentInteractionService } from "../services/component-interaction/component-interaction.service";
import { ColorCodes } from "@utils/color-constants";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
    ModelQuantityJson,
    StructureBaseModel,
    StructureInstance,
    StructureResponseMessage,
    StructureStatus,
} from "@models/structure.model";
import { MatDialog } from "@angular/material/dialog";
import { GenericDialogComponent } from "../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { DialogService } from "../services/dialog/dialog.service";
import { StructureService } from "../services/structure/structure.service";
import { filter, distinctUntilChanged, merge, scan, Subscription, switchMap, take, startWith } from "rxjs";
import { Location } from "@angular/common";
import { GenerateNewShapes } from "../shared/generate-new-shapes";
import { CountCalculatorService } from "../services/count-calculator.service";
import {
    CountInputData,
    CountOutputData,
    Face,
    FaceType,
    MaterialName,
} from "../../../../utils/count-calculator/count-calculator.model";
import { environment } from "~/src/environments/environment";
import { S3UploadService } from "../services/s3-upoad/s3-upload.service";
import { ActionHelperService } from "../action-helper.service";
import { SceneManagerService } from "../services/scene-manager/scene-manager.service";
import { RouteTrackingService } from "../services/route-tracking/route-tracking.service";
import { ShapeSelectorComponent } from "./shape-selector/shape-selector.component";
import { FilenameService } from "../services/filename/filename.service";
import { ShapeResourceManagerService } from "../shared/helpers/shape-resource-manager.service";
import { UndoRedoService } from "../services/undoRedo/undo-redo.service";
import { Action, UndoRedoAction } from "~/src/app/shared/models/undoRedo.model";
import { AuxiliaryObjectType, MeshType } from "~/src/utils/shape-type";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { CSG } from "three-csg-ts";
import { NodeHoleConfiguration } from "../shared/models/shape.config.model";
import { ActionButtonContainer, ColorHEX, SaveButton } from "../../../../utils/action";
import { InvalidShapeService } from "../services/invalid-shape.service";
import { CustomTourService } from "../services/custom-tour.service";
import { DesignerTourSteps } from "~/src/app/shared/models/designer-tour.model";
import { UserService } from "../services/user/user.service";
import { AdminStatus } from "@models/admin.model";
import { CopyPasteService } from "../services/drag-and-drop/copypaste.service";
import { InteractionService } from "../shared/helpers/interaction.service";
import { FaceMetaDataMap } from "~/src/utils/shape-facetype";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "../services/auth/auth.service";
import { ActionButtonContainerComponent } from './action-button-container/action-button-container.component';

type RenderItem = {
    scene: Scene;
    camera: PerspectiveCamera;
    mesh: Mesh;
    controls: OrbitControls;
    canvas: HTMLCanvasElement;
};

@Component({
    selector: "new-canvas",
    templateUrl: "./canvascomponent.component.html",
    styleUrls: ["./canvascomponent.component.scss"],
    standalone: false,
})
export class CanvasComponent implements OnInit, AfterViewInit, OnDestroy {
    // ViewChild and Input declarations
    @ViewChild("main_canvas", { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChildren("shape_canvas") shapecCanvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;
    @ViewChild("shapeSelector") shapeSelectorRef!: ShapeSelectorComponent;
    @ViewChild('actionContainer') private actionContainer!: ActionButtonContainerComponent;
    @Input() isViewMode = false;
    @Input() isGlb = false;
    public isCaptureImageLoaded = false;
    public isLoadingShape: boolean = false;
    private scene!: Scene;
    private camera!: PerspectiveCamera;
    private renderer: WebGLRenderer | null = null;
    private orbitControl!: OrbitControls;
    private plane!: Mesh;
    private gridHelper!: GridHelper;
    public meshes: Mesh[] = [];
    public selectedUuid: string | null = null;
    public isEditMode: boolean = false;
    public isEditted: boolean = false;
    public zoomConfig = ZoomConfig;
    public pushCapturedImage: File[] = [];
    public isDisabledCaptureBtn: boolean = false;
    public isDisabledCapture360Btn: boolean = false;
    public autoCapturedImageUrl!: string;
    public structureData!: StructureInstance;
    public structureStatus = StructureStatus;
    public isLoading: boolean = false;
    public isFromProductDetail: boolean = false;
    public outputPattern!: CountOutputData[];
    public structureId: number | null = null;
    private capturedImageLength = 0;
    public showBackButton: boolean = true;
    public dataSource!: CountOutputData[];
    // Subscriptions
    private subscriptions: Subscription[] = [];
    private selectionBox: HTMLElement | null = null;
    public displayedColumns = ["name", "facePattern", "count", "shape"];
    public DesignerTourSteps = DesignerTourSteps;
    private hasCompletedTour = false;
    private sharedRenderer: WebGLRenderer | null = null;
    private renderQueue: RenderItem[] = [];
    private animationFrameId: number | null = null;
    public isAdminRoute: boolean = false;
    public isUndoActive: boolean = false;
    public isRedoActive: boolean = false;
    public isDeleteActive: boolean = false;
    public selectedMeshes: Mesh[] = [];
    public canUndo: boolean = false;
    public canRedo: boolean = false;
    public isTourStepActive: boolean = false;
    public isSmallerScreen: boolean = false;
    public isStepTourActive: boolean = false;

    public saveBtns = [
        {
            iconlabel: SaveButton.saveStructureIconLabel,
            iconHint1: SaveButton.saveStructureIconHint,
        },
        {
            iconlabel: SaveButton.updateStructureIconLabel,
            iconHint1: SaveButton.updateStructureIconHint,
        },
        {
            iconlabel: SaveButton.publishIconLabel,
            iconHint1: SaveButton.publishIconHint,
        },
        {
            iconlabel: SaveButton.loadIconLabel,
            iconHint1: SaveButton.loadIconHint,
        },
    ];

    constructor(
        private componentInteractionService: ComponentInteractionService,
        private interactionService: InteractionService,
        private router: Router,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private structureService: StructureService,
        private route: ActivatedRoute,
        private location: Location,
        private generateShapeService: GenerateNewShapes,
        private countCalculatorService: CountCalculatorService,
        private s3UploadService: S3UploadService,
        private actionHelperService: ActionHelperService,
        private sceneManagerService: SceneManagerService,
        private routeTrackingService: RouteTrackingService,
        private filenameService: FilenameService,
        private shapeResourceManagerService: ShapeResourceManagerService,
        private undoRedoService: UndoRedoService,
        private customTourService: CustomTourService,
        private userService: UserService,
        private invalidService: InvalidShapeService,
        private cdRef: ChangeDetectorRef,
        public copyPasteService: CopyPasteService,
        private translate: TranslateService,
        private auth: AuthService,
    ) {
        effect(() => {
            this.canUndo = this.undoRedoService.canUndo();
            this.canRedo = this.undoRedoService.canRedo();
            this.hasCompletedTour = this.customTourService.hasCompletedTour();
        });

        this.customTourService.openActionsCall$.subscribe((res) => {
            this.isStepTourActive = false;
        });

        /*** This will trigger when the step tour is Active ***/
        this.customTourService.stepShow$.subscribe((res) => {
            this.isStepTourActive = true;
        });

        this.isSmallerScreen = window.innerWidth > 864; // To check the screen size is greater than 9 inch
    }

    ngOnInit(): void {
        this.shapeResourceManagerService.initializeResources(false);
        this.componentInteractionService.meshSelections$.subscribe((selections) => {
            this.selectedMeshes = selections.map((selection) => selection.mesh);
        });

        this.sceneManagerService.meshes$.subscribe((meshes) => {
            this.meshes = meshes;
            this.interactionService.render();
        });

        this.isAdminRoute = this.router.url.includes("admin");

        this.route.queryParamMap.subscribe((params) => {
            const from = params.get("from");
            if (from === "mail") {
                this.showBackButton = false;
            } else {
                this.showBackButton = true;
            }
        });
        this.route.queryParams.subscribe((params) => {
            this.isFromProductDetail = !!params["fromProductDetail"];
            if (!this.isFromProductDetail && !this.isAdminRoute) {
                if (this.auth.isUserLoggedIn && this.showBackButton) {
                    this.userService.getUserDetails().subscribe((response) => {
                        if (response?.user) {
                            if (response?.user.hasCompletedTour) {
                                this.customTourService.hasCompletedTour.set(response?.user.hasCompletedTour);
                            } else {
                                this.customTourService.hasCompletedTour.set(response?.user.hasCompletedTour!);
                                this.openOnBoardingDialog();
                            }
                        } else {
                            this.openOnBoardingDialog();
                        }
                    });
                }
            }
        });
        // Subscription to track step changes
        this.customTourService.stepShow$
            .pipe(
                filter((stepChangeParams) => {
                    this.isTourStepActive = true; /**** Check Step Tour is active if not allow to do action  ******/
                    return (
                        this.auth.isUserLoggedIn &&
                        !this.hasCompletedTour &&
                        !!stepChangeParams.step.anchorId &&
                        this.customTourService.isLastStep(stepChangeParams.step.anchorId)
                    );
                }),
                take(1),
                switchMap(() => this.userService.markTourAsCompleted()),
            )
            .subscribe(() => {
                this.customTourService.hasCompletedTour.set(true);
            });
        this.customTourService.openActionsCall$.subscribe((response) => {
            this.isTourStepActive = false; /*** Set step tour as inactive after closing the popUp ***/
        });

        /*** This will received after the capture Image is added to S3 ***/
        this.structureService.loadImage$.subscribe((response) => {
            this.isCaptureImageLoaded = response;
        });
    }

    async ngAfterViewInit(): Promise<void> {
        await this.initializeCanvas();
        if (!this.auth.isUserLoggedIn && !this.isAdminRoute && !this.isFromProductDetail && this.isSmallerScreen) {
            this.loginDialog();
        }
        this.setupCameraPosition();
        this.sceneManagerService.setScene(this.scene);
        this.sceneManagerService.addObjectsToScene([this.gridHelper, this.plane]);
        this.setupEventListeners();
        this.setupSubscriptions();
        this.selectionBox = document.getElementById("selectionBox") as HTMLElement;

        if (this.renderer) {
            this.interactionService.initialize(
                this.scene,
                this.camera,
                this.renderer,
                this.orbitControl,
                this.selectionBox,
            );
        }

        const waitForData = () => {
            if (!this.dataSource?.length || !this.meshes?.length) {
                requestAnimationFrame(waitForData);
            } else {
                this.isLoading = true;
                this.cdRef.detectChanges();
                this.processShapes();
            }
        };

        waitForData();
    }

    // Track resources for disposal
    private disposables: {
        geometries: BufferGeometry[];
        materials: Material[];
        scenes: Scene[];
    } = {
        geometries: [],
        materials: [],
        scenes: [],
    };

    private processShapes(): void {
        this.cleanupPreviousRender();
        if (!this.sharedRenderer) {
            this.initSharedRenderer();
        }

        // Step 1: Create dataSource map using name + facePattern + normalized color
        const dataSourceMap = new Map<string, { element: CountInputData; index: number }>();
        this.dataSource.forEach((element: CountInputData, index: number) => {
            const colorValues = Object.values(element.color || {}).map((color: any) => color.toLowerCase());
            colorValues.sort(); // Normalize color order
            const colorKey = colorValues.join("-");

            const key = `${element.name}_${element.facePattern}_${colorKey}`;
            dataSourceMap.set(key, { element, index });
        });

        // Step 2: Prepare uniqueShapesMap using the same key logic
        const uniqueShapesMap = new Map<string, { mesh: Mesh; dataIndex: number }>();

        this.meshes.forEach((mesh) => {
            const facePattern = mesh.userData[Object3DUserData.facePattern];
            if (!facePattern) return;

            const material = (mesh.material as MeshBasicMaterial[]) || [];
            const colorValues = material
                .filter(
                    (material: MeshBasicMaterial) =>
                        material?.name && material?.color && MaterialName.includes(material.name),
                )
                .map((material: MeshBasicMaterial) => "#".concat(material.color.getHexString().toLowerCase()));
            colorValues.sort(); // Normalize

            const colorKey = colorValues.join("-");

            const matchKey = `${mesh.name}_${facePattern}_${colorKey}`;
            const matchingData = dataSourceMap.get(matchKey);
            if (matchingData && !uniqueShapesMap.has(matchKey)) {
                uniqueShapesMap.set(matchKey, { mesh: mesh, dataIndex: matchingData.index });
            }
        });

        const uniqueShapes = Array.from(uniqueShapesMap.values());
        const canvasElements = this.shapecCanvasRefs.map((ref) => ref.nativeElement);
        const minCount = Math.min(canvasElements.length, uniqueShapes.length);

        this.batchProcessShapes(canvasElements as unknown as NodeListOf<HTMLCanvasElement>, uniqueShapes, minCount);
    }

    private cleanupPreviousRender(): void {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        this.renderQueue.forEach((item) => {
            if (item.controls) {
                item.controls.dispose();
            }
        });

        this.renderQueue = [];
    }

    private batchProcessShapes(
        canvases: NodeListOf<HTMLCanvasElement>,
        uniqueShapes: { mesh: Mesh; dataIndex: number }[],
        minCount: number,
    ) {
        const batchSize = 5; // Process 5 shapes at a time
        let processed = 0;

        const processBatch = () => {
            const start = processed;
            const end = Math.min(processed + batchSize, minCount);

            for (let i = start; i < end; i++) {
                try {
                    this.initThreeScene(canvases[i] as HTMLCanvasElement, uniqueShapes[i].mesh);
                } catch (error) {
                    throw error;
                }
            }

            processed = end;

            if (processed < minCount) {
                requestAnimationFrame(processBatch);
            } else {
                this.startSharedAnimationLoop();
                this.isLoading = false;
                this.cdRef.detectChanges();
            }
        };
        processBatch();
    }

    public loginDialog(): void {
        this.router.navigate(["/login"]);
    }

    private initSharedRenderer() {
        const tempCanvas = document.createElement("canvas");
        this.sharedRenderer = new WebGLRenderer({
            canvas: tempCanvas,
            antialias: true,
            powerPreference: "high-performance",
        });
    }

    private initThreeScene(canvas: HTMLCanvasElement, meshObject: Mesh) {
        canvas.width = 100;
        canvas.height = 100;

        const scene = new Scene();
        scene.background = new Color(ColorCodes.lightGray);
        this.disposables.scenes.push(scene);

        const camera = new PerspectiveCamera(75, canvas.width / canvas.height, 0.1, 1000);
        camera.position.set(0, 0, 30);

        const light = new AmbientLight(ColorCodes.pointLight);
        scene.add(light);

        const clonedMesh = meshObject.clone(true);
        clonedMesh.geometry = meshObject.geometry.clone();
        this.disposables.geometries.push(clonedMesh.geometry);

        if (Array.isArray(meshObject.material)) {
            clonedMesh.material = meshObject.material.map((mat) => {
                const clonedMat = mat.clone();
                this.disposables.materials.push(clonedMat);
                return clonedMat;
            });
        } else {
            clonedMesh.material = meshObject.material.clone();
            this.disposables.materials.push(clonedMesh.material);
        }

        clonedMesh.position.set(0, 0, 0);
        scene.add(clonedMesh);

        try {
            this.addNodesAndHoles(clonedMesh, scene);
        } catch (error) {
            throw error;
        }

        // Attach controls to the correct canvas
        const controls = new OrbitControls(camera, canvas);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.rotateSpeed = 0.5;
        controls.enableZoom = true;
        controls.enablePan = true;

        this.renderQueue.push({
            scene,
            camera,
            mesh: clonedMesh,
            controls,
            canvas,
        });
    }

    private startSharedAnimationLoop() {
        let lastTime = 0;
        const targetFPS = 30; // Limit to 30 FPS for better performance
        const frameInterval = 1000 / targetFPS;

        const animate = (time: number) => {
            this.animationFrameId = requestAnimationFrame(animate);
            const deltaTime = time - lastTime;
            if (deltaTime < frameInterval) return;
            lastTime = time - (deltaTime % frameInterval);
            // Render only visible canvases
            this.renderVisibleShapes();
        };
        animate(0);
    }

    private renderVisibleShapes() {
        if (!this.sharedRenderer) return;

        this.renderQueue.forEach((item) => {
            const { scene, camera, mesh, controls, canvas } = item;

            if (this.isElementInViewport(canvas)) {
                controls.update();

                // Auto-rotate only when not interacting
                mesh.rotation.x += 0.01;
                mesh.rotation.y += 0.01;

                this.sharedRenderer!.setSize(canvas.width, canvas.height, false);
                this.sharedRenderer!.render(scene, camera);

                const context = canvas.getContext("2d");
                if (context) {
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(this.sharedRenderer!.domElement, 0, 0);
                }
            }
        });
    }

    private isElementInViewport(el: HTMLElement): boolean {
        const rect = el.getBoundingClientRect();
        return (
            rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.bottom >= 0 &&
            rect.left <= (window.innerWidth || document.documentElement.clientWidth) &&
            rect.right >= 0
        );
    }

    /**
     * Adds nodes and holes based on userData.faceType
     */
    private addNodesAndHoles(mesh: Mesh, scene: Scene) {
        if (!mesh.userData || !mesh.userData[Object3DUserData.faceMetaDataMap]) return;

        const faceMetaDataMap = mesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap;

        const boundingBox = new Box3().setFromObject(mesh);
        const size = new Vector3();
        boundingBox.getSize(size);

        // Add lighting once
        if (!scene.userData["lightingAdded"]) {
            scene.add(new AmbientLight(ColorCodes.pointLight, 0.5));
            const dirLight = new DirectionalLight(ColorCodes.pointLight, 0.8);
            dirLight.position.set(1, 1, 1).normalize();
            scene.add(dirLight);
            scene.userData["lightingAdded"] = true;
        }

        const faceData = {
            Top: { position: new Vector3(0, 1, 0), rotation: new Euler(0, 0, 0) },
            Bottom: { position: new Vector3(0, -1, 0), rotation: new Euler(Math.PI, 0, 0) },
            Front: { position: new Vector3(1, 0, 0), rotation: new Euler(Math.PI / 2, 0, 0) },
            Back: { position: new Vector3(-1, 0, 0), rotation: new Euler(-Math.PI / 2, 0, 0) },
            Left: { position: new Vector3(0, 0, -1), rotation: new Euler(0, 0, Math.PI / 2) },
            Right: { position: new Vector3(0, 0, 1), rotation: new Euler(0, 0, -Math.PI / 2) },
        } as Record<string, { position: Vector3; rotation: Euler }>;

        const baseBox = new BoxGeometry(5, 5, 5);
        const nodeMaterial = new MeshStandardMaterial({ color: ColorHEX.greencyan });
        const holeMaterial = new MeshStandardMaterial({ color: ColorHEX.greencyan });

        const isFrontOrBack = (face: string) => face === Face.Front || face === Face.Back;
        const isLeftRight = (face: string) => face === Face.Left || face === Face.Right;

        const getOffsetFactor = (type: FaceType, meshName: string, face: string): number => {
            const frontOrBack = isFrontOrBack(face);
            const leftRight = isLeftRight(face);

            if (type === FaceType.Node) {
                if (meshName === MeshType.HemiSphere) return NodeHoleConfiguration.Offset;
                if (meshName === MeshType.HalfCylinder)
                    return leftRight ? NodeHoleConfiguration.Offset : NodeHoleConfiguration.Offset;
                // if (meshName === MeshType.ConcavePrism)
                //     return leftRight ? NodeHoleConfiguration.DefaultOffsetFactor : NodeHoleConfiguration.Offset;
                return NodeHoleConfiguration.DefaultOffsetFactor;
            }

            // Hole
            if (meshName === MeshType.HemiSphere)
                return this.isHemisphereOnTop(mesh)
                    ? NodeHoleConfiguration.ObjectPositionNode
                    : NodeHoleConfiguration.Offset;
            if (meshName === MeshType.HalfCylinder)
                return leftRight
                    ? NodeHoleConfiguration.OffsetHalfCylinder
                    : this.isHemisphereOnTop(mesh)
                    ? NodeHoleConfiguration.ObjectPositionNode
                    : NodeHoleConfiguration.Offset;
            // if (meshName === MeshType.ConcavePrism)
            //     return leftRight
            //         ? NodeHoleConfiguration.DefaultOffsetFactor
            //         : NodeHoleConfiguration.DefaultOffsetFactor;
            if (meshName === MeshType.CurvedCube2)
                return frontOrBack ? NodeHoleConfiguration.OffsetHoleFactor : NodeHoleConfiguration.DefaultOffsetFactor;
            if (meshName === MeshType.Prism)
                return face === Face.Right
                    ? NodeHoleConfiguration.OffsetHoleFactor
                    : NodeHoleConfiguration.DefaultOffsetFactor;
            if (meshName === MeshType.CurvedCube)
                return leftRight ? NodeHoleConfiguration.OffsetHoleFactor : NodeHoleConfiguration.DefaultOffsetFactor;

            return NodeHoleConfiguration.DefaultOffsetFactor;
        };

        const adjustPosition = (face: Face, meshName: string, pos: Vector3) => {
            if (meshName === MeshType.RightTriangle && isLeftRight(face)) {
                pos.x = -NodeHoleConfiguration.ObjectPosition;
            }
            // if (meshName === MeshType.ConcavePrism && isLeftRight(face)) {
            //     pos.x = -NodeHoleConfiguration.ObjectPositionX;
            //     pos.y = -NodeHoleConfiguration.ObjectPositionY;
            // }
            if (meshName === MeshType.HemiSphere || meshName === MeshType.HalfCylinder) {
                pos.y =
                    faceMetaDataMap[face].type === FaceType.Node || faceMetaDataMap[face].type === FaceType.Hole
                        ? -NodeHoleConfiguration.ObjectPositionNode
                        : -NodeHoleConfiguration.ObjectPositionYHemisphereHalfcylinder;
            }
        };

        Object.entries(faceMetaDataMap).forEach(([faceKey, value]) => {
            const face = faceKey as Face;
            const faceInfo = faceData[face];
            if (!faceInfo) return;

            const typeValue = value.type as FaceType;

            const localPosition = faceInfo.position.clone();
            adjustPosition(face, mesh.name, localPosition);

            const offsetFactor = getOffsetFactor(typeValue, mesh.name, face);
            const offset = size.clone().multiply(localPosition).multiplyScalar(offsetFactor);

            if (typeValue === FaceType.Node) {
                const nodeMesh = new Mesh(baseBox.clone(), nodeMaterial);
                nodeMesh.setRotationFromEuler(faceInfo.rotation);
                nodeMesh.position.copy(offset);
                mesh.add(nodeMesh);
            }

            if (typeValue === FaceType.Hole) {
                // const holeSize =
                //     mesh.name === MeshType.ConcavePrism && !isLeftRight(face)
                //         ? new BoxGeometry(5, 1, 5)
                //         : baseBox.clone();
                const holeSize = baseBox.clone();
                const holeMesh = new Mesh(holeSize, holeMaterial);
                holeMesh.setRotationFromEuler(faceInfo.rotation);
                holeMesh.position.copy(offset);
                holeMesh.updateMatrixWorld(true);
                holeMesh.applyMatrix4(mesh.matrixWorld);

                const newMesh = CSG.subtract(mesh, holeMesh);
                const extractedHole = CSG.intersect(mesh, holeMesh);

                const wireframe = new WireframeGeometry(extractedHole.geometry);
                const line = new LineSegments(wireframe);
                line.material = new LineBasicMaterial({ color: ColorCodes.darkRed });
                mesh.add(line);

                mesh.geometry.dispose();
                mesh.geometry = newMesh.geometry;
                mesh.geometry.computeVertexNormals();
            }
        });

        mesh.visible = true;
    }

    public openOnBoardingDialog(): void {
        if (this.isViewMode || !this.isSmallerScreen) {
            return;
        }
        this.dialogService.onBoardingDialog();
    }

    private isHemisphereOnTop(mesh: Mesh): boolean {
        return JSON.stringify(mesh.userData[Object3DUserData.orientationData]) === JSON.stringify(initialOrientation);
    }

    // Scene Initialization Methods
    private async initializeCanvas(): Promise<void> {
        await this.runAsyncTasks([
            this.initializeScene(),
            this.initializeCamera(),
            this.initializeRenderer(),
            this.initializeOrbitControl(),
            this.createPlaneAndGrid(),
        ]);

        this.animate();
    }

    // Helper function to run async tasks sequentially
    private async runAsyncTasks(tasks: Promise<void>[]): Promise<void> {
        for (const task of tasks) {
            await task; // Ensure each task is awaited one after another
        }
    }

    private async initializeScene(): Promise<void> {
        this.scene = new Scene();
        this.scene.background = new Color(ColorCodes.sceneBackground);
        this.sceneManagerService.setScene(this.scene);
    }

    private async initializeCamera(): Promise<void> {
        const { width, height } = this.getContainerDimensions();
        this.camera = new PerspectiveCamera(
            CanvasConfig.perspectiveCameraFov,
            width / height,
            CanvasConfig.perspectiveCameraNearField,
            CanvasConfig.perspectiveCameraFarField,
        );
    }

    private setupCameraPosition(): void {
        this.camera.position.set(
            CanvasConfig.cameraPosition_X_Cooridinate,
            CanvasConfig.cameraPosition_Y_Cooridinate,
            CanvasConfig.cameraPosition_Z_Cooridinate,
        );

        this.componentInteractionService.setCameraPosition(this.camera.position.clone());
    }

    private async initializeRenderer(): Promise<void> {
        if (!this.canvasRef) return;

        if (this.renderer) {
            this.renderer.forceContextLoss();
            this.renderer.dispose();
        }

        this.renderer = new WebGLRenderer({
            canvas: this.canvasRef.nativeElement,
            antialias: true,
            alpha: true,
        });

        const { width, height } = this.getContainerDimensions();
        this.renderer.setSize(width, height, false);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.domElement.tabIndex = 0;
    }

    // Helper function to get the container's dimensions
    private getContainerDimensions(): { width: number; height: number } {
        const container = this.canvasRef.nativeElement;
        return { width: container.clientWidth, height: container.clientHeight };
    }

    private async initializeOrbitControl(): Promise<void> {
        if (!this.camera || !this.renderer) return;

        this.orbitControl = new OrbitControls(this.camera, this.renderer.domElement);
        this.configureOrbitControls();
    }

    private configureOrbitControls(): void {
        const config = CanvasConfig;
        this.orbitControl.enabled = true;
        this.orbitControl.enableZoom = true;
        this.orbitControl.minDistance = config.orbitControlMinDistance;
        this.orbitControl.maxDistance = config.orbitControlMaxDistance;
        this.orbitControl.target = new Vector3(
            config.orbitControlTarget_X_Coordinate,
            config.orbitControlTarget_Y_Coordinate,
            config.orbitControlTarget_Z_Coordinate,
        );
        this.orbitControl.rotateSpeed = config.oribitControlRotateSpeed;
        this.orbitControl.zoomSpeed = config.oribitControlZoomSpeed;
        this.orbitControl.panSpeed = config.oribitControlPanSpeed;
        this.orbitControl.enableDamping = true;
        this.orbitControl.dampingFactor = config.orbitControlDampingFactor;
        this.orbitControl.maxPolarAngle = Math.PI / config.orbitControlMaxPolarAngleDivisor;
    }

    private async createPlaneAndGrid(): Promise<void> {
        const canvasSize = CanvasConfig.divisions * CanvasConfig.cellSize;

        // Create plane
        this.plane = new Mesh(
            new PlaneGeometry(canvasSize, canvasSize),
            new MeshBasicMaterial({ color: 0xFFFFFF, side: DoubleSide }),
        );
        this.plane.rotateX(-Math.PI / 2);
        this.plane.position.y = CanvasConfig.planePostionIn_Y_Coordinate - CanvasConfig.planePositionOffset;
        this.plane.name = AuxiliaryObjectType.Plane;

        // Create grid
        this.gridHelper = new GridHelper(canvasSize, CanvasConfig.divisions);
        this.gridHelper.name = AuxiliaryObjectType.GridHelper;
        this.gridHelper.position.y = CanvasConfig.planePostionIn_Y_Coordinate;
    }

    private onWindowResize(): void {
        const { width, height } = this.getContainerDimensions();
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        if (this.renderer) {
            this.renderer.setSize(width, height, false);
        }
        this.interactionService.render();
    }

    private handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (this.isEditted) {
            event.preventDefault();
            event.returnValue = ""; // Standard way to trigger the prompt
        }
    };

    // Handle OrbitControls change event
    private onOrbitControlChange = (): void => {
        // Update the compass cube with the new rotation
        this.componentInteractionService.setCameraPosition(this.camera.position.clone());
        this.interactionService.render();
    };

    private onMouseWheel = (event: WheelEvent) => {
        if (event.deltaY < 0) {
            this.handleMouseZoom(-CanvasConfig.zoomDefaultValue);
        } else {
            this.handleMouseZoom(CanvasConfig.zoomDefaultValue);
        }
    };

    // Event Listeners Setup
    private setupEventListeners(): void {
        window.addEventListener("resize", this.onWindowResize.bind(this));
        window.addEventListener("beforeunload", this.handleBeforeUnload);
        this.canvasRef.nativeElement.addEventListener("wheel", this.onMouseWheel.bind(this));
        this.orbitControl?.addEventListener("change", this.onOrbitControlChange);
    }

    private setupSubscriptions(): void {
        this.setupGeneratedShapeSubscription();
        this.setupRouteSubscriptions();
        this.setupStateChangeSubscriptions();
        this.setupStructureImageSubscription();
        this.setupOutputPatternSubscription();
        if (this.renderer && !this.isViewMode) {
            this.interactionService.subscribeToEvents(this.renderer, this.camera);
        }
    }

    private setupGeneratedShapeSubscription(): void {
        const generatedShapeSub = this.componentInteractionService.getGeneratedShapeInfo().subscribe((shape) => {
            this.handleGeneratedShape(shape as Mesh);
        });
        this.subscriptions.push(generatedShapeSub);
    }

    private setupStateChangeSubscriptions(): void {
        const merged$ = this.componentInteractionService.reset$.pipe(
            startWith(null),
            switchMap(() =>
                merge(
                    this.interactionService.isEditted$,
                    this.generateShapeService.isNewShapeGenerated$,
                    this.actionHelperService.isRotated$,
                ).pipe(
                    scan((acc, current) => acc || current, false),
                    distinctUntilChanged(),
                ),
            ),
        );

        const isEdittedSub = merged$.pipe(filter(() => this.isEditMode)).subscribe((value) => {
            this.isEditted = value;
        });

        this.subscriptions.push(isEdittedSub);
    }

    private setupStructureImageSubscription(): void {
        const structureImageSub = this.structureService.structureImage$.subscribe(() => {
            this.capturedImageLength = this.structureService.getImagesCount();
            this.isDisabledCaptureBtn = this.capturedImageLength > 9;
        });
        this.subscriptions.push(structureImageSub);
    }

    private setupOutputPatternSubscription(): void {
        const outputPatternSub = this.componentInteractionService.getOutputPattern().subscribe((res) => {
            this.outputPattern = res;
        });
        this.subscriptions.push(outputPatternSub);
    }

    private handleGeneratedShape(mesh: Mesh): void {
        this.sceneManagerService.addObjectsToScene([mesh]);
        const action: UndoRedoAction = {
            actionType: Action.Add,
            mesh: mesh,
        };
        this.undoRedoService.addToUndo(action);
        this.isEditted = true;
    }

    public resetIsEditted(data: boolean) {
        this.isEditted = data;
    }

    private setupRouteSubscriptions(): void {
        const paramSub = this.route.paramMap.subscribe((paramMap) => {
            if (paramMap.has("id")) {
                this.resetCanvas();
                this.handleStructureIdParam(+paramMap.get("id")!);
            }
        });

        const querySub = this.route.queryParamMap.subscribe((queryParamMap) => {
            if (queryParamMap.has("id")) {
                this.resetCanvas();
                this.handleStructureIdQuery(+queryParamMap.get("id")!);
            }
        });

        this.subscriptions.push(paramSub, querySub);
    }

    private resetCanvas(): void {
        this.sceneManagerService.removeAllMeshesFromScene();
        this.isEditted = false;
    }

    public handleStructureIdParam(id: number): void {
        this.isEditMode = true;
        this.structureId = id;
        this.loadStructure(this.structureId);
    }

    private handleStructureIdQuery(id: number): void {
        if (id) {
            this.isEditMode = true;
            this.structureId = id;
            this.loadStructure(this.structureId);
        } else if (!this.isEditMode) {
            this.isEditMode = false;
            this.structureId = null;
        }
    }

    public loadStructure(structureId: number): void {
        this.showSpinner(true); // Show the spinner before starting the operation
        this.structureService.getStructureDetails(structureId, this.isFromProductDetail, this.isAdminRoute).subscribe({
            next: (res) => {
                if (!res || !res.structureData) {
                    this.showSpinner(false);
                    const redirectPath = this.isAdminRoute ? "/admin/noAccess" : "/noAccess";
                    this.router.navigate([redirectPath], { replaceUrl: true });
                    return;
                }
                this.structureData = res.structureData;
                const url = this.structureData.structureData;
                const isUpdated = this.structureData.isUpdated;
                if (url) {
                    const extension = this.sceneManagerService.getFileExtension(url);
                    switch (extension) {
                        case "json":
                            this.sceneManagerService
                                .loadFromJsonUrl(url, isUpdated)
                                .then(() => {
                                    this.interactionService.render();
                                    this.calculation();
                                })
                                .finally(() => {
                                    this.showSpinner(false);
                                });
                            break;
                        case "glb":
                            this.sceneManagerService
                                .loadFromGlbUrl(url, isUpdated)
                                .then(() => {
                                    this.interactionService.render();
                                    this.calculation();
                                })
                                .finally(() => {
                                    this.showSpinner(false);
                                });
                            break;
                        default:
                            break;
                    }
                } else {
                    this.showSpinner(false);
                }
            },
            error: () => {
                this.showSpinner(false);
            },
        });
    }

    private animate(): void {
        requestAnimationFrame(() => this.animate());
        this.orbitControl.update();
    }

    // Public Methods To ZoomIN & ZoomOut
    public zoom(direction: string): void {
        switch (direction) {
            case this.zoomConfig.zoomIn:
                this.handleClickZoom(-CanvasConfig.zoomDefaultValue);
                break;
            case this.zoomConfig.zoomOut:
                this.handleClickZoom(CanvasConfig.zoomDefaultValue);
                break;
        }
        this.interactionService.render();
    }

    // Called when user clicks zoom in or out
    private handleClickZoom(zoomDirection: number): void {
        this.zoomCamera(zoomDirection, true);
    }

    // Called on mouse wheel or drag
    private handleMouseZoom(wheelDelta: number): void {
        this.zoomCamera(wheelDelta, false);
    }

    // distanceDelta value comes -(negative) when zoomin , +(positive) when zoom out
    private zoomCamera(distanceDelta: number, isZoomByClick: boolean): void {
        const currentDistance = Math.round(this.camera.position.distanceTo(this.orbitControl.target));
        let newDistance = Math.max(
            this.orbitControl.minDistance,
            Math.min(currentDistance + distanceDelta, this.orbitControl.maxDistance),
        );
        const isOutOfBound =
            (currentDistance === this.orbitControl.minDistance && distanceDelta < 0) ||
            (currentDistance === this.orbitControl.maxDistance && distanceDelta > 0);

        if (isOutOfBound) {
            this.showZoomLimitMessage(distanceDelta < 0);
        } else {
            this.orbitControl.enableZoom = true;
        }
        const clickDelta = newDistance - currentDistance;
        if (isZoomByClick) {
            const direction = new Vector3().subVectors(this.camera.position, this.orbitControl.target).normalize();
            direction.multiplyScalar(clickDelta);
            this.camera.position.add(direction);
            this.camera.updateProjectionMatrix();
        }
    }

    private showZoomLimitMessage(isZoomIn: boolean): void {
        const text = isZoomIn ? "in" : "out";
        const template = this.zoomConfig.zoomLimitMessage;
        const message = template.replace("${direction}", text);
        this.orbitControl.enableZoom = false;
        this.snackBar.open(message, SnackBarConfig.Actions.CLOSE, {
            duration: SnackBarConfig.Duration.MEDIUM,
        });
    }

    public redirectPage() {
        const meshes = this.sceneManagerService.getMeshes();
        if (this.isEditted && meshes.length && this.auth.isUserLoggedIn && !this.isFromProductDetail) {
            this.openDialog();
        } else {
            this.goBack();
        }
    }

    private openDialog(): void {
        const structureStatus = this.structureData?.status;
        const isPublishedOrApproved = [StructureStatus.Published, StructureStatus.Approved].includes(
            structureStatus as StructureStatus,
        );

        const dialogRef = this.dialog.open(GenericDialogComponent, {
            disableClose: true,
            data: {
                componentName: DialogInvokingComponents.SaveDesignConfirmation,
                title: this.isEditMode
                    ? isPublishedOrApproved
                        ? "Save Design Confirmation"
                        : "Update Design Confirmation"
                    : "Save Design Confirmation",
                content: `Do you want to ${
                    this.isEditMode && !isPublishedOrApproved ? "update" : "save"
                } this structure?`,
                firstBtn: "Yes",
                secondBtn: "No",
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe(async (saveClicked) => {
            if (saveClicked === true) {
                this.interactionService.deselectAllMeshes();
                if (!this.meshes.length) {
                    this.openSnackBar(StructureResponseMessage.NoStructureFound, SnackBarConfig.Actions.OKAY);
                    return;
                } else {
                    const hasInvalidShapes = await this.invalidService.checkInvalid();
                    if (hasInvalidShapes) {
                        this.openSnackBar(
                            this.getTranslation("designer.invalidMessage.shapeIsNotValid"),
                            SnackBarConfig.Actions.OKAY,
                        );
                        return;
                    }
                    const data = {
                        componentName: DialogInvokingComponents.StructureDetails,
                        title: this.isEditMode
                            ? isPublishedOrApproved
                                ? "Save Structure"
                                : "Update Structure"
                            : "Save Structure",
                        firstBtn: this.isEditMode ? (isPublishedOrApproved ? "Save" : "Update") : "Save",
                        secondBtn: "Cancel",
                        structureName: this.isEditMode
                            ? isPublishedOrApproved
                                ? ""
                                : this.structureData?.structureName || ""
                            : "",
                        structureDescription: this.isEditMode
                            ? isPublishedOrApproved
                                ? ""
                                : this.structureData?.structureDescription || ""
                            : "",
                    };

                    this.dialogService
                        .openDialog(data)
                        .afterClosed()
                        .subscribe(async (response) => {
                            if (!response) {
                                return;
                            }

                            const file = this.autoCaptureCanvas();
                            const urls = await this.s3UploadService.multipartOperation([file], "structures", "images");
                            this.autoCapturedImageUrl = urls[0];
                            this.countCalculatorService.calculate(this.meshes);
                            const fileUrl = await this.s3UploadService.uploadStructureDataToS3(this.meshes, "models");
                            const modelBody: ModelQuantityJson = {
                                totalcount: this.meshes.length,
                                faceinformation: this.outputPattern,
                            };
                            const modelUrl = await this.s3UploadService.uploadStructureDataToS3(
                                modelBody,
                                "model-Info",
                            );
                            const body: StructureBaseModel = {
                                structureName: response.structureName,
                                structureDescription: response.structureDescription,
                                structureData: fileUrl,
                                isUpdated: true,
                                status: StructureStatus.Saved,
                                imageurl: this.autoCapturedImageUrl,
                                modelQuantityJson: {
                                    modelInfoUrl: modelUrl,
                                    totalBlock: this.meshes.length,
                                },
                            };
                            if (this.isEditMode && !isPublishedOrApproved) {
                                this.structureService.updateStructure(this.structureData?.id, body).subscribe({
                                    next: (response) => {
                                        this.openSnackBar(response.message, SnackBarConfig.Actions.OKAY);
                                        this.router.navigateByUrl("/account/myStructures");
                                    },
                                    error: (error) => {
                                        this.openSnackBar(error.error, SnackBarConfig.Actions.OKAY);
                                    },
                                });
                            } else {
                                this.structureService.saveStructure(body).subscribe({
                                    next: (response) => {
                                        this.openSnackBar(response.message, SnackBarConfig.Actions.OKAY);
                                        this.router.navigateByUrl("/account/myStructures");
                                    },
                                    error: (error) => {
                                        this.openSnackBar(error.error, SnackBarConfig.Actions.OKAY);
                                    },
                                });
                            }
                        });
                }
            } else {
                this.goBack();
            }
        });
    }

    private goBack(): void {
        const previousUrl = this.routeTrackingService.getPreviousUrl();
        this.location.back(); // First back
        if (previousUrl === "/login") {
            setTimeout(() => {
                // Optional: Only go back second time if still at or near login
                if (this.router.url === "/login" || previousUrl === "/login") {
                    this.location.back(); // Second back
                }
            }, 0);
        }
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    // For download
    public captureImage(): void {
        if (this.isDisabledCaptureBtn) {
            return;
        }
        // Get all visible structures (excluding the plane)
        const relevantObjects = this.scene.children.filter(
            (child) => child instanceof Mesh && child !== this.plane && child.visible,
        );

        // Setup frustum for visibility check
        const frustum = new Frustum();
        const cameraViewProjectionMatrix = new Matrix4();

        this.camera.updateMatrixWorld();
        this.camera.updateProjectionMatrix();
        cameraViewProjectionMatrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
        frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

        // Function to check if at least 30% of an object is in view
        const isSignificantlyVisible = (object: Object3D): boolean => {
            const boundingBox = new Box3().setFromObject(object);
            const points = [
                new Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.min.z),
                new Vector3(boundingBox.min.x, boundingBox.min.y, boundingBox.max.z),
                new Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.min.z),
                new Vector3(boundingBox.min.x, boundingBox.max.y, boundingBox.max.z),
                new Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.min.z),
                new Vector3(boundingBox.max.x, boundingBox.min.y, boundingBox.max.z),
                new Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.min.z),
                new Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.max.z),
            ];

            let visiblePoints = 0;
            const totalPoints = points.length;
            const projectedPoint = new Vector3();

            for (const point of points) {
                projectedPoint
                    .copy(point)
                    .applyMatrix4(this.camera.matrixWorldInverse)
                    .applyMatrix4(this.camera.projectionMatrix);
                if (
                    projectedPoint.x >= -1 &&
                    projectedPoint.x <= 1 &&
                    projectedPoint.y >= -1 &&
                    projectedPoint.y <= 1
                ) {
                    visiblePoints++;
                }
            }

            // If at least 30% of the object is inside the camera view, consider it visible
            return visiblePoints / totalPoints >= 0.3;
        };

        const isAnyObjectSignificantlyVisible = relevantObjects.some(isSignificantlyVisible);

        if (!isAnyObjectSignificantlyVisible) {
            this.openSnackBar(AdminStatus.FailedToCapture, SnackBarConfig.Actions.CLOSE);
            return;
        }

        // Create offscreen renderer with same dimensions and settings
        const offscreenRenderer = new WebGLRenderer({
            alpha: true,
            antialias: true,
        });
        offscreenRenderer.setSize(this.renderer!.domElement.width, this.renderer!.domElement.height);

        // Clone the scene to avoid modifying original
        const sceneCopy = this.scene.clone();

        // Find and remove grid helper and plane from cloned scene
        const gridHelperForCanvas = sceneCopy.children.find((child) => child instanceof GridHelper);
        const planeMesh = sceneCopy.children.find((child) => child instanceof Mesh && child.name === this.plane.name);

        if (gridHelperForCanvas) sceneCopy.remove(gridHelperForCanvas);
        if (planeMesh) sceneCopy.remove(planeMesh);

        // Set up transparent background
        sceneCopy.background = null;
        offscreenRenderer.setClearAlpha(0);

        // Render the scene with the current camera
        offscreenRenderer.render(sceneCopy, this.camera);

        // Capture the image
        const imgData = offscreenRenderer.domElement.toDataURL("image/png");
        const filename = `image_${Date.now()}.png`;
        const file = this.dataURLToFile(imgData, filename);

        // Clean up
        offscreenRenderer.dispose();

        // Add the image to the structure service
        this.structureService.addImage(file);
    }

    private dataURLToFile(dataURL: string, filename: string): File {
        const [header, data] = dataURL.split(",");
        const mime = header.match(/:(.*?);/)![1];
        const binaryString = atob(data);
        const arrayBuffer = new ArrayBuffer(binaryString.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < binaryString.length; i++) {
            uint8Array[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([uint8Array], { type: mime });
        return new File([blob], filename, { type: mime });
    }

    public autoCaptureCanvas() {
        // Create a new renderer for off-screen rendering
        const offscreenRenderer = new WebGLRenderer({
            alpha: true, // Enable alpha channel
            antialias: true,
        });
        offscreenRenderer.setSize(this.renderer!.domElement.width, this.renderer!.domElement.height);

        // Create a clone of the scene
        const sceneCopy = this.scene.clone();

        // Find and remove grid helper and plane from the cloned scene
        const gridHelper = sceneCopy.children.find((child) => child instanceof GridHelper);
        const planeMesh = sceneCopy.children.find((child) => child instanceof Mesh && child.name === this.plane.name);

        if (gridHelper) sceneCopy.remove(gridHelper);
        if (planeMesh) sceneCopy.remove(planeMesh);

        // Set up camera for the screenshot
        const cameraCopy = this.camera.clone();
        cameraCopy.position
            .set(
                CanvasConfig.cameraPosition_X_Cooridinate,
                CanvasConfig.cameraPosition_Y_Cooridinate,
                CanvasConfig.cameraPosition_Z_Cooridinate,
            )
            .addScalar(10);
        cameraCopy.lookAt(new Vector3(0, 0, 0));
        cameraCopy.updateProjectionMatrix();

        // Clear background (make it transparent)
        sceneCopy.background = null;

        // Render the scene
        offscreenRenderer.render(sceneCopy, cameraCopy);

        // Get the image data with PNG format to preserve transparency
        const imgData = offscreenRenderer.domElement.toDataURL("image/png");
        const fileName = this.filenameService.generateTimestampFileName("image", "png");
        const file = this.dataURLToFile(imgData, fileName);

        // Clean up
        offscreenRenderer.dispose();
        offscreenRenderer.forceContextLoss();

        return file;
    }

    public async captureScene() {
        this.showSpinner(true);
        await this.sceneManagerService
            .exportToS3("structures", "glb-files")
            .then((url) => {
                const glbUrl = url;
                this.isDisabledCapture360Btn = true;
                this.structureService.captureGlbUrl(glbUrl);
                this.openSnackBar(StructureResponseMessage.Capture360, SnackBarConfig.Actions.OKAY);
            })
            .catch((error) => {
                this.openSnackBar(error.message, SnackBarConfig.Actions.OKAY);
            })
            .finally(() => {
                this.showSpinner(false);
            });
    }

    // Cleanup
    ngOnDestroy(): void {
        this.cleanupEventListeners();
        this.cleanupSubscriptions();

        // Dispose of Three.js resources
        this.dispose();

        this.copyPasteService.removeCopyObjects();
        this.customTourService.hasCompletedTour.set(false);
        this.cleanupResources();
        this.clearHighlights();
        this.undoRedoService.clearUndoRedoStack();
        this.interactionService.deselectAllMeshes();
    }

    private cleanupResources() {
        // Cancel animation frame
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Dispose of all controls
        this.renderQueue.forEach((item) => {
            if (item.controls) {
                item.controls.dispose();
            }
        });

        // Dispose of geometries
        this.disposables.geometries.forEach((geometry) => {
            if (geometry && !geometry.dispose) {
                geometry.dispose();
            }
        });

        // Dispose of materials
        this.disposables.materials.forEach((material) => {
            if (material) {
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat.dispose());
                } else {
                    material.dispose();
                }
            }
        });

        // Clear scenes
        this.disposables.scenes.forEach((scene) => {
            scene.clear();
        });

        // Dispose of renderer
        if (this.sharedRenderer) {
            this.sharedRenderer.dispose();
            this.sharedRenderer = null;
        }

        // Clear arrays
        this.renderQueue = [];
        this.disposables.geometries = [];
        this.disposables.materials = [];
        this.disposables.scenes = [];
    }

    private cleanupEventListeners(): void {
        window.removeEventListener("resize", this.onWindowResize);
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
        this.canvasRef.nativeElement.removeEventListener("wheel", this.onMouseWheel);
        this.orbitControl?.removeEventListener("change", this.onOrbitControlChange);
    }

    private cleanupSubscriptions(): void {
        this.unsubscribeToInteractiveEvents();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private unsubscribeToInteractiveEvents(): void {
        this.interactionService.unsubscribeToEvents();
    }

    private dispose(): void {
        // Dispose of renderer
        if (this.renderer) {
            this.renderer.forceContextLoss();
            this.renderer.dispose();
            this.renderer = null;
        }

        // Clear scene
        this.sceneManagerService.removeAllObjectsFromScene();
    }

    public showSpinner(show: boolean): void {
        this.isLoading = show;
    }

    public calculation(): void {
        this.countCalculatorService.calculate(this.meshes);
        const componentInteractionSub = this.componentInteractionService.getOutputPattern().subscribe((res) => {
            this.outputPattern = res;
        });
        this.subscriptions.push(componentInteractionSub);
        if (this.outputPattern) {
            this.isLoading = false;
            this.dataSource = this.outputPattern;
        }
    }

    public highlightShape(uuids: string | string[]): void {
        const uuidArray = Array.isArray(uuids) ? uuids : [uuids];

        this.meshes.forEach((mesh) => {
            if (uuidArray.includes(mesh.uuid)) {
                this.applyHighlight(mesh);
            } else {
                this.makeTransparent(mesh);
            }
        });

        this.selectedUuid = uuids as any;
        this.renderer?.render(this.scene, this.camera);
    }

    private applyHighlight(mesh: Mesh): void {
        if (!mesh.material) return;

        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

        materials.forEach((material) => {
            if (material instanceof MeshStandardMaterial || material instanceof MeshBasicMaterial) {
                material.color.set(ColorCodes.red);
                material.opacity = 0.5;
                material.transparent = true;
                material.needsUpdate = true;
            }
        });
    }

    private makeTransparent(mesh: Mesh): void {
        if (!mesh.material) return;

        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

        materials.forEach((material) => {
            if (material instanceof MeshStandardMaterial || material instanceof MeshBasicMaterial) {
                material.color.set(ColorCodes.transparentBlue);
                material.opacity = 0.3;
                material.transparent = true;
                material.needsUpdate = true;
            }
        });
    }

    private removeHighlight(mesh: Mesh): void {
        const originalColors = mesh.userData[Object3DUserData.originalColors];
        if (originalColors && Array.isArray(originalColors)) {
            if (mesh instanceof Mesh) {
                // Check if the shape is a mesh and handle multi-material case
                if (Array.isArray(mesh.material)) {
                    mesh.material.forEach((material: Material, index: number) => {
                        if (material instanceof MeshBasicMaterial && originalColors[index]) {
                            const changedColor = new Color(originalColors[index]);
                            material.color.copy(changedColor);
                            material.transparent = false;
                            material.opacity = 1;
                        }
                    });
                }
            }
        }
    }

    // Listen for clicks on the canvas only
    @HostListener("dblclick", ["$event"])
    onCanvasClick(event: Event): void {
        // Check if there is a highlighted shape before clearing
        if (this.selectedUuid && this.canvasRef?.nativeElement === event.target) {
            this.clearHighlights();
        }
    }
    // Button to go to home page if screen size is less than 9 inch (864px)
    public closeWarning(): void {
        this.router.navigate(["/home"]);
    }

    private clearHighlights(): void {
        this.meshes.forEach((mesh) => this.removeHighlight(mesh));
        this.selectedUuid = null;
    }

    get isNotFromProductDetail(): boolean {
        return !this.isFromProductDetail;
    }

    public getSelectedId(id: number): void {
        this.resetCanvas();
        this.handleStructureIdParam(id);
    }

    /**** To do Delete Shape Action ****/
    public clickDeleteIcon(): void {
        if (this.selectedMeshes.length) {
            this.interactionService.deleteMeshes(this.selectedMeshes as Mesh[]);
            this.isUndoActive = false;
            this.isRedoActive = false;
            this.isDeleteActive = true;
        }
    }

    /**** To do Undo Action ****/
    public clickUndoIcon(): void {
        this.interactionService.deselectAllMeshes();
        this.undoRedoService.performUndo();
        this.isDeleteActive = false;
    }

    /**** To do Redo Action ****/
    public clickRedoIcon(): void {
        this.undoRedoService.performRedo();
    }

    /**** To do Copy Action ****/
    public clickCopyIcon(): void {
        this.copyPasteService.copyMultipleObjects(this.selectedMeshes);
    }

    /**** To do Paste Action ****/
    public clickPasteIcon(): void {
        this.copyPasteService.handleMouseClick();
    }

    /**** To do Duplicate Action ****/
    public clickDuplicateIcon(): void {
        this.interactionService.duplicateStructure();
    }

    public getcountDialogBox(): void {
        this.outputPattern = [];
        if (this.meshes.length) {
            this.showSpinner(true);
            setTimeout(() => {
                try {
                    this.countCalculatorService.calculate(this.meshes);
                    const dialogRef = this.dialogService.openDialog(
                        {componentName: DialogInvokingComponents.GetCountDialog, outputPattern: this.outputPattern},
                        "90%",
                        { disableClose: true , maxHeight: "none" },
                        "1300px"
                    );
                    dialogRef.afterClosed().subscribe((res) => {
                        if (!res) this.outputPattern = [];
                    });
                } finally {
                    this.showSpinner(false);
                }
            }, 100);
        }
    }

    public saveActions(iconValue: SaveButton): void {
        this.actionContainer.saveActions(iconValue);
    }

    get filteredSaveBtns() {
        return this.saveBtns.filter(
            (button) =>
                button.iconlabel === SaveButton.publishIconLabel ||
                (this.isEditMode &&
                    this.structureData?.status !== StructureStatus.Published &&
                    this.structureData?.status !== StructureStatus.Approved &&
                    button.iconlabel === SaveButton.updateStructureIconLabel) ||
                ((!this.isEditMode ||
                    this.structureData?.status === StructureStatus.Published ||
                    this.structureData?.status === StructureStatus.Approved) &&
                    button.iconlabel === SaveButton.saveStructureIconLabel) ||
                button.iconlabel === SaveButton.loadIconLabel,
        );
    }

    public isDisabled(btn: { iconlabel: SaveButton; iconHint1: SaveButton }): boolean {
        const disabledConditions: Partial<Record<SaveButton, () => boolean>> = {
            [SaveButton.saveStructureIconHint]: () => !this.auth.isUserLoggedIn && !this.meshes.length,
            [SaveButton.updateStructureIconHint]: () => !this.isEditted,
            [SaveButton.publishIconHint]: () =>
                (!!this.structureData?.status && this.structureData?.status !== StructureStatus.Saved) ||
                (!this.auth.isUserLoggedIn && !this.meshes.length),
        };

        return disabledConditions[btn.iconHint1]?.() ?? false;
    }
}
